import React, { memo, useCallback, useEffect } from 'react';
import { Box, Typography, Grid, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import FormTextField from './FormTextField';
import FormSelectField from './FormSelectField';
import FormCheckbox from './FormCheckbox';
import { fiscalDataSchema } from './validationSchema';

const defaultInitialValues = {
  documentType: '',
  documentNumber: '',
};

const FiscalDataSection = memo(
  ({
    index,
    background,
    useCommonFiscalData,
    commonFiscalDataPassengerIndex,
    commonFiscalData,
    onToggleCommonData,
    handleCommonFiscalDataChange,
    handleIndividualFiscalDataChange,
    updateFiscalData,
    initialValues,
    totalPassengers,
  }) => {
    const safeInitialValues = initialValues || defaultInitialValues;

    const formik = useFormik({
      initialValues: useCommonFiscalData ? commonFiscalData : safeInitialValues,
      validationSchema: fiscalDataSchema,
      validateOnMount: false,
      validateOnChange: true,
      validateOnBlur: true,
    });

    // Actualizar valores cuando cambian los datos comunes o cuando se desactiva el uso de datos comunes
    useEffect(() => {
      if (useCommonFiscalData) {
        formik.setValues({
          documentType: commonFiscalData.documentType,
          documentNumber: commonFiscalData.documentNumber,
        });
        
        // Cuando usamos datos comunes, validar el formulario para mostrar errores
        if (index === commonFiscalDataPassengerIndex) {
          formik.validateForm().then(errors => {
            const hasErrors = Object.keys(errors).length > 0;
            // Si hay errores en el pasajero principal, actualizar todos los pasajeros
            if (hasErrors) {
              handleCommonFiscalDataChange('validationStatus', hasErrors);
            }
          });
        }
      } else if (initialValues) {
        // Cuando se desactiva useCommonFiscalData, volver a los valores iniciales específicos de este pasajero
        formik.setValues({
          documentType: initialValues.documentType || '',
          documentNumber: initialValues.documentNumber || '',
        });
        
        // Resetear toques y errores cuando cambia el estado
        if (formik.touched.documentType || formik.touched.documentNumber) {
          formik.setTouched({
            documentType: false,
            documentNumber: false
          });
        }
      }
    }, [useCommonFiscalData, commonFiscalData, index, commonFiscalDataPassengerIndex, initialValues]);

    const handleChange = useCallback(
      (e) => {
        const { name, value } = e.target;
        formik.setFieldValue(name, value, true);

        if (useCommonFiscalData) {
          handleCommonFiscalDataChange(name, value);
        } else {
          handleIndividualFiscalDataChange(index, name, value);
        }
      },
      [useCommonFiscalData, handleCommonFiscalDataChange, handleIndividualFiscalDataChange, index, formik],
    );

    // Determine input type based on document type
    const getInputTypeForDocument = useCallback((docType) => {
      switch (docType) {
        case 'dni':
          return 'numeric';
        case 'pasaporte':
          return 'alphanumeric';
        case 'cedula':
          return 'numeric';
        default:
          return undefined;
      }
    }, []);

    const handleBlur = useCallback(
      (e) => {
        const { name } = e.target;
        formik.handleBlur(e);
        formik.setFieldTouched(name, true, true);
        
        // Validar después de blur y actualizar el estado de validación
        formik.validateField(name).then(() => {
          const errors = formik.errors;
          const hasErrors = Object.keys(errors).length > 0;
          
          if (useCommonFiscalData && index === commonFiscalDataPassengerIndex) {
            // Propagar el estado de validación a través de los datos comunes
            handleCommonFiscalDataChange('validationStatus', hasErrors);
          } else if (!useCommonFiscalData) {
            // Actualizar solo este pasajero
            const updatedData = {
              documentType: formik.values.documentType,
              documentNumber: formik.values.documentNumber,
            };
            updateFiscalData(index, updatedData, false, hasErrors);
          }
        });
      },
      [formik, index, useCommonFiscalData, commonFiscalDataPassengerIndex, handleCommonFiscalDataChange, updateFiscalData],
    );

    const handleToggleCommonData = useCallback(
      (e) => {
        const isChecked = e.target.checked;
        const currentValues = formik.values;
        
        // Validar el formulario actual antes de compartir datos
        formik.validateForm().then(errors => {
          const hasErrors = Object.keys(errors).length > 0;
          onToggleCommonData(index, isChecked ? currentValues : null, hasErrors);
        });
      },
      [index, formik, onToggleCommonData],
    );

    const getHelperText = (documentType) => {
      switch (documentType) {
        case 'dni':
          return 'Ingrese solo números, 7-8 dígitos';
        case 'pasaporte':
          return 'Ingrese letras y números, 6-12 caracteres';
        case 'cedula':
          return 'Ingrese solo números, 6-10 dígitos';
        default:
          return '';
      }
    };

    // Determinar si se deben mostrar errores
    const showErrors = useCommonFiscalData 
      ? index === commonFiscalDataPassengerIndex // Solo mostrar errores en el pasajero principal cuando se usan datos comunes
      : true; // Mostrar errores individuales cuando no se usan datos comunes
      
    return (
      <Grid item xs={12} data-testid="fiscal-data-container">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Datos fiscales
          </Typography>
          {totalPassengers > 1 && (
            <>
              {useCommonFiscalData && commonFiscalDataPassengerIndex !== index ? (
                <Typography
                  variant="body1"
                  sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}
                >
                  Se está usando los datos del Pasajero {commonFiscalDataPassengerIndex + 1}
                </Typography>
              ) : (
                <FormCheckbox
                  checked={useCommonFiscalData}
                  onChange={handleToggleCommonData}
                  label={
                    useCommonFiscalData
                      ? `Usar datos del Pasajero ${commonFiscalDataPassengerIndex + 1}`
                      : 'Usar datos en todos los pasajeros'
                  }
                  background={background}
                />
              )}
            </>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormSelectField
              name="documentType"
              label="Tipo *"
              disabled={useCommonFiscalData && index !== commonFiscalDataPassengerIndex}
              formik={formik}
              onChange={handleChange}
              onBlur={handleBlur}
              error={showErrors && formik.touched.documentType && Boolean(formik.errors.documentType)}
              helperText={showErrors && formik.touched.documentType ? formik.errors.documentType : ''}
            >
              <MenuItem value="dni">DNI</MenuItem>
              <MenuItem value="pasaporte">Pasaporte</MenuItem>
              <MenuItem value="cedula">Cédula</MenuItem>
            </FormSelectField>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormTextField
              name="documentNumber"
              label="Número *"
              disabled={(useCommonFiscalData && index !== commonFiscalDataPassengerIndex) || !formik.values.documentType}
              formik={formik}
              helperText={
                (showErrors && formik.touched.documentNumber && formik.errors.documentNumber) || 
                getHelperText(formik.values.documentType)
              }
              error={showErrors && formik.touched.documentNumber && Boolean(formik.errors.documentNumber)}
              onChange={handleChange}
              onBlur={handleBlur}
              inputType={getInputTypeForDocument(formik.values.documentType)}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  },
);

FiscalDataSection.displayName = 'FiscalDataSection';

export default FiscalDataSection;
